import styled, { css } from "styled-components";
import { useDesignTokens } from "@boi/react-design-tokens";
import { TickIcon, MinusIcon } from "@boi/react-icons";

type CustomCheckboxProps = {
  $hasError?: boolean;
};

export const CustomCheckbox = styled.div<CustomCheckboxProps>`
  ${({ $hasError }) => {
    const { tokens } = useDesignTokens();
    const { checkbox } = tokens;

    return [
      css`
        position: relative;
        display: grid;
        place-items: center;
        width: ${checkbox.width};
        height: ${checkbox.height};
        border-radius: ${checkbox.stroke.radius};
        outline-style: solid;
        outline-offset: -1px;
        cursor: pointer;

        &:has(input:disabled) {
          cursor: not-allowed;
        }

        &:has(input:not(:checked)) {
          background: ${checkbox.backgroundColor.unchecked.base};
          outline-width: ${checkbox.stroke.width.unchecked.base};
          outline-color: ${checkbox.stroke.color.base};
        }

        &:hover:has(input:not(:disabled, :checked, :indeterminate)),
        :is(label:hover, [role="option"][data-hovered]):has(
            input:not(:disabled, :checked, :indeterminate)
          )
          && {
          background: ${checkbox.backgroundColor.unchecked.hover};
          outline-width: ${checkbox.stroke.width.unchecked.hover};
          outline-color: ${checkbox.stroke.color.hover};
        }

        &:has(input:not(:checked):disabled) {
          background: ${checkbox.backgroundColor.unchecked.disabled};
          outline-width: ${checkbox.stroke.width.unchecked.disabled};
          outline-color: ${checkbox.stroke.color.disabled};
        }

        &:has(input:checked) {
          background: ${checkbox.backgroundColor.checked.base};
          outline-width: ${checkbox.stroke.width.checked.base};
          outline-color: ${checkbox.stroke.color.base};
        }

        &:hover:has(input:not(:disabled):checked),
        :is(label:hover, [role="option"][data-hovered]):has(
            input:not(:disabled):checked
          )
          && {
          background: ${checkbox.backgroundColor.checked.hover};
          outline-width: ${checkbox.stroke.width.checked.hover};
          outline-color: ${checkbox.stroke.color.hover};
        }

        &:has(input:checked:disabled) {
          background: ${checkbox.backgroundColor.checked.disabled};
          outline-width: ${checkbox.stroke.width.checked.disabled};
          outline-color: ${checkbox.stroke.color.disabled};
        }

        &:has(input:indeterminate) {
          background: ${checkbox.backgroundColor.checked.base};
          outline-width: ${checkbox.stroke.width.checked.base};
          outline-color: ${checkbox.stroke.color.base};
        }

        &:hover:has(input:not(:disabled):indeterminate),
        :is(label:hover, [role="option"][data-hovered]):has(
            input:not(:disabled):indeterminate
          )
          && {
          background: ${checkbox.backgroundColor.checked.hover};
          outline-width: ${checkbox.stroke.width.checked.hover};
          outline-color: ${checkbox.stroke.color.hover};
        }

        &:has(input:indeterminate:disabled) {
          background: ${checkbox.backgroundColor.checked.disabled};
          outline-width: ${checkbox.stroke.width.checked.disabled};
          outline-color: ${checkbox.stroke.color.disabled};
        }
      `,
      $hasError &&
        css`
          &:has(input:not(:checked)) {
            outline-width: ${checkbox.stroke.width.unchecked.error};
            outline-color: ${checkbox.stroke.color.error};
          }

          &:hover:has(input:not(:disabled, :checked, :indeterminate)),
          :is(label:hover, [role="option"][data-hovered]):has(
              input:not(:disabled, :checked, :indeterminate)
            )
            && {
            outline-width: ${checkbox.stroke.width.unchecked.hover};
            outline-color: ${checkbox.stroke.color.hover};
          }

          &:has(input:checked) {
            outline-width: ${checkbox.stroke.width.checked.error};
            outline-color: ${checkbox.stroke.color.error};
          }

          &:hover:has(input:not(:disabled, :indeterminate):checked),
          :is(label:hover, [role="option"][data-hovered]):has(
              input:not(:disabled, :indeterminate):checked
            )
            && {
            outline-width: ${checkbox.stroke.width.checked.hover};
            outline-color: ${checkbox.stroke.color.hover};
          }
        `,
    ];
  }}
`;

export const HiddenInput = styled.input`
  cursor: inherit;
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  margin: 0;
  padding: 0;
  z-index: 1;
`;

export const IndeterminateIcon = styled(MinusIcon)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { checkbox } = tokens;

    return [
      css`
        display: none;

        ${CustomCheckbox}:has(input:indeterminate) && {
          display: block;
          width: ${checkbox.icon.width};
          height: ${checkbox.icon.height};
          color: ${checkbox.icon.color.base};
        }
      `,
    ];
  }}
`;

export const CheckedIcon = styled(TickIcon)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { checkbox } = tokens;

    return [
      css`
        display: none;

        ${CustomCheckbox}:has(input:not(:indeterminate):checked) && {
          display: block;
          width: ${checkbox.icon.width};
          height: ${checkbox.icon.height};
          color: ${checkbox.icon.color.base};
        }
      `,
    ];
  }}
`;
